import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getTopArticles, getPublishedArticles, subscribeToActiveAds } from '../firebase/firestore';
import { timeAgo } from '../utils/dateHelpers';
import './Sidebar.css';

const AD_ROTATE_INTERVAL = 5000; // ms between sidebar ad rotations

export default function Sidebar() {
  const [topArticles, setTopArticles] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [sidebarAds, setSidebarAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [adFading, setAdFading] = useState(false);
  const rotateTimerRef = useRef(null);

  useEffect(() => {
    getTopArticles(6).then(setTopArticles);
    getPublishedArticles(6).then(setLatestArticles);
    const unsub = subscribeToActiveAds('sidebar', ads => {
      setSidebarAds(ads || []);
      setCurrentAdIndex(0);
    });
    return unsub;
  }, []);

  // Auto-rotate sidebar ads when there are multiple
  useEffect(() => {
    if (sidebarAds.length <= 1) return;

    rotateTimerRef.current = setInterval(() => {
      setAdFading(true);
      setTimeout(() => {
        setCurrentAdIndex(prev => (prev + 1) % sidebarAds.length);
        setAdFading(false);
      }, 400);
    }, AD_ROTATE_INTERVAL);

    return () => clearInterval(rotateTimerRef.current);
  }, [sidebarAds]);

  const currentAd = sidebarAds[currentAdIndex] || null;

  return (
    <aside className="sidebar">
      {currentAd && (
        <div className="sidebar-ad">
          <span className="ad-label-small">Ad</span>
          <a
            href={currentAd.linkURL}
            target="_blank"
            rel="noopener noreferrer"
            className={`sidebar-ad-link ${adFading ? 'fading' : ''}`}
          >
            <img src={currentAd.imageURL} alt={currentAd.title} />
          </a>
          {/* Dots navigation for multiple ads */}
          {sidebarAds.length > 1 && (
            <div className="sidebar-ad-dots">
              {sidebarAds.map((_, i) => (
                <button
                  key={i}
                  className={`sidebar-ad-dot ${i === currentAdIndex ? 'active' : ''}`}
                  onClick={() => setCurrentAdIndex(i)}
                  aria-label={`Ad ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="sidebar-widget">
        <h3 className="widget-title">🔥 Most Read</h3>
        {topArticles.map((a, i) => (
          <Link key={a.id} to={`/article/${a.id}`} className="sidebar-article">
            <span className="sidebar-num">{String(i+1).padStart(2,'0')}</span>
            <div className="sidebar-article-info">
              <p className="sidebar-article-title">{a.title}</p>
              <span className="sidebar-article-meta">{a.category} · {a.viewCount || 0} views</span>
            </div>
          </Link>
        ))}
      </div>

      <div className="sidebar-widget">
        <h3 className="widget-title">🕐 Latest News</h3>
        {latestArticles.map(a => (
          <Link key={a.id} to={`/article/${a.id}`} className="sidebar-latest">
            {a.thumbnailURL && <img src={a.thumbnailURL} alt={a.title} />}
            <div>
              <p className="sidebar-latest-title">{a.title}</p>
              <span className="sidebar-article-meta">{timeAgo(a.createdAt)}</span>
            </div>
          </Link>
        ))}
      </div>

      <div className="sidebar-widget epaper-cta">
        <div className="epaper-cta-inner">
          <span className="epaper-icon">📰</span>
          <h3>Today's E-Paper</h3>
          <p>Read the digital edition of our newspaper</p>
          <Link to="/epaper" className="btn btn-primary btn-sm">Read E-Paper →</Link>
        </div>
      </div>
    </aside>
  );
}